import axios from "axios";
import { SERVER_URL } from "../../databucket";
import {
  GET_WEBTOP_FILES,
  GET_WEBTOP_FILE,
  GET_WEBTOP_STORAGE,
  GET_WEBTOP_DESKTOP_FOLDER,
  GET_WEBTOP_TRASH,
  CREATE_WEBTOP_NODE,
  COPY_WEBTOP_NODE,
  UPDATE_WEBTOP_NODE,
  DELETE_WEBTOP_NODE,
  RESTORE_WEBTOP_NODE,
  PURGE_WEBTOP_NODE,
  EMPTY_WEBTOP_TRASH,
} from "../../Queries/webtopFiles";

/**
 * filesApi — thin wrapper over the WebtopFile GraphQL + the existing S3 upload
 * routes. Every call carries the drive `scope` (personal | organization |
 * department); the server enforces access from the JWT. The Apollo client is
 * imported lazily (like desktopLayoutSlice) so tests don't execute src/index.js.
 */
const getClient = async () => (await import("../../index")).client;

// Fire after any file mutation so the desktop icons (and other file views)
// refresh immediately instead of waiting for a window-focus event.
const notifyFilesChanged = () => {
  try {
    if (typeof window !== "undefined") window.dispatchEvent(new Event("webtop:files-changed"));
  } catch (e) { /* no-op */ }
};

export const listFiles = async (scope, parentId = null) => {
  const client = await getClient();
  const { data } = await client.query({
    query: GET_WEBTOP_FILES,
    variables: { scope, parentId: parentId || null },
    fetchPolicy: "no-cache",
  });
  return data?.getWebtopFiles || [];
};

export const getStorage = async () => {
  const client = await getClient();
  const { data } = await client.query({ query: GET_WEBTOP_STORAGE, fetchPolicy: "no-cache" });
  const map = {};
  (data?.getWebtopStorage || []).forEach((r) => {
    map[r.scope] = r.used || 0;
  });
  return map;
};

export const createFolder = async (name, scope, parentId = null) =>
  createNode({ name, type: "folder", scope, parentId });

export const createNode = async (vars) => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: CREATE_WEBTOP_NODE, variables: vars });
  notifyFilesChanged();
  return data?.createWebtopNode;
};

export const updateNode = async (vars) => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: UPDATE_WEBTOP_NODE, variables: vars });
  notifyFilesChanged();
  return data?.updateWebtopNode;
};

export const copyNode = async (id, targetScope, targetParentId = null) => {
  const client = await getClient();
  const { data } = await client.mutate({
    mutation: COPY_WEBTOP_NODE,
    variables: { id, targetScope, targetParentId: targetParentId || null },
  });
  notifyFilesChanged();
  return data?.copyWebtopNode;
};

/**
 * pasteNode — paste a clipboard entry into a target folder/drive.
 *  - cut within the same drive → move (updateNode parentId)
 *  - otherwise → copy; if it was a cut, remove the original (to Recycle Bin)
 */
export const pasteNode = async (clip, targetScope, targetParentId = null) => {
  if (clip.mode === "cut" && clip.scope === targetScope) {
    return updateNode({ id: clip.id, parentId: targetParentId || null });
  }
  const copied = await copyNode(clip.id, targetScope, targetParentId);
  if (clip.mode === "cut") await deleteNode(clip.id);
  return copied;
};

export const deleteNode = async (id) => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: DELETE_WEBTOP_NODE, variables: { id } });
  notifyFilesChanged();
  return data?.deleteWebtopNode;
};

export const getFile = async (id) => {
  const client = await getClient();
  const { data } = await client.query({
    query: GET_WEBTOP_FILE,
    variables: { id },
    fetchPolicy: "no-cache",
  });
  return data?.getWebtopFile || null;
};

export const updateFileContent = async (id, content) => updateNode({ id, content });

export const getDesktopFolder = async () => {
  const client = await getClient();
  const { data } = await client.query({ query: GET_WEBTOP_DESKTOP_FOLDER, fetchPolicy: "no-cache" });
  return data?.getWebtopDesktopFolder || null;
};

export const listTrash = async () => {
  const client = await getClient();
  const { data } = await client.query({ query: GET_WEBTOP_TRASH, fetchPolicy: "no-cache" });
  return data?.getWebtopTrash || [];
};

export const restoreNode = async (id) => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: RESTORE_WEBTOP_NODE, variables: { id } });
  notifyFilesChanged();
  return data?.restoreWebtopNode;
};

export const purgeNode = async (id) => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: PURGE_WEBTOP_NODE, variables: { id } });
  return data?.purgeWebtopNode;
};

export const emptyTrash = async () => {
  const client = await getClient();
  const { data } = await client.mutate({ mutation: EMPTY_WEBTOP_TRASH });
  return data?.emptyWebtopTrash;
};

/**
 * uploadAndCreate — uploads a real File to S3 via the existing /upload route,
 * then records a file node in the given drive pointing at the returned URL.
 */
export const uploadAndCreate = async (file, scope, parentId = null) => {
  const form = new FormData();
  form.append("files", file);
  const res = await axios.post(`${SERVER_URL}/upload`, form);
  const uploaded = (res?.data?.files || [])[0];
  if (!uploaded?.url) throw new Error("Upload failed");
  return createNode({
    name: uploaded.originalName || file.name,
    type: "file",
    scope,
    parentId,
    url: uploaded.url,
    mimeType: uploaded.mimeType || file.type || "",
    size: file.size || 0,
  });
};

/**
 * shareTextToDrive — saves a Notepad doc / sticky note as an editable text file
 * (type "textfile", content stored in the dashboard) into a drive/folder. Opens
 * later in Notepad; shareable org/department-wide via its scope.
 */
export const shareTextToDrive = async (name, content, scope, parentId = null) => {
  const base = String(name || "note").trim() || "note";
  const fname = base.toLowerCase().endsWith(".txt") ? base : `${base}.txt`;
  return createNode({ name: fname, type: "textfile", scope, parentId, content: content || "" });
};

// Proxy download/preview URL for a file (S3 via the /download route).
export const fileDownloadUrl = (node, mode = "attachment") =>
  `${SERVER_URL}/download?url=${encodeURIComponent(node.url)}&name=${encodeURIComponent(
    node.name || "file"
  )}&mode=${mode}`;

export const isImage = (node) => (node.mimeType || "").startsWith("image/");
// A spreadsheet (.sheet) is stored as a textfile whose content is workbook JSON.
// Check this BEFORE isText so the File Explorer opens it in Sheets, not Notepad.
export const isSheet = (node) => (node.name || "").toLowerCase().endsWith(".sheet");
export const isText = (node) =>
  !isSheet(node) &&
  (node.type === "textfile" ||
    (node.mimeType || "").startsWith("text/") ||
    (node.name || "").toLowerCase().endsWith(".txt"));

// Create a new (empty) spreadsheet file in a drive/folder. `emptyContent` is the
// serialized blank workbook (passed in to avoid a circular import on sheetModel).
export const createSheetFile = async (name, scope, parentId, emptyContent) => {
  const base = String(name || "Spreadsheet").trim() || "Spreadsheet";
  const fname = base.toLowerCase().endsWith(".sheet") ? base : `${base}.sheet`;
  return createNode({ name: fname, type: "textfile", scope, parentId: parentId || null, content: emptyContent || "" });
};

// Fetch a text file's content (for the read-only preview).
export const fetchText = async (node) => {
  const res = await axios.get(fileDownloadUrl(node, "inline"), { responseType: "text" });
  return typeof res.data === "string" ? res.data : String(res.data ?? "");
};
